import React, {ReactElement, useState} from 'react';
import {styled} from "@mui/material/styles";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import Grid from "@mui/material/Grid";
import * as Yup from "yup"
import {yupResolver} from "@hookform/resolvers/yup";
import Typography from "@mui/material/Typography";
import {useAppDispatch} from "../App/hooks/store";
import {AuthActions} from "./actions/auth";
import {IAuthState} from "./interface/auth";
import {AccountActions} from "../Account/actions/account";
import {TextField} from "../App/components/Input/TextField";
import {Link} from "../App/components/Link/Link";
import {Button} from "../App/components/Button";
import {Field} from "../App/components/Form/Field";
import {Confirmation} from "./components/Authorize/Confirmation";

const Description = styled(Typography)(() => ({
  color: "rgb(148, 163, 184)"
}))

const Title = styled(Typography)(() => ({
  fontWeight: "500",
  fontSize: "1.3rem"
}))

const Logo = styled(Typography)(({theme}) => ({
  fontWeight: "600",
  fontSize: "1.5rem",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3)
}))

interface Props {
  setType: (option: 'login' | 'registration' | 'recovery') => void,
  onClose: () => void
}

const schema = Yup
  .object({
    email: Yup.string().email('Некорректный формат электронной почты').required("Введите адрес электронной почты"),
    password: Yup.string().required("Введите пароль")
  }).required()

export function Login(props: Props): ReactElement | null {
  const dispatch = useAppDispatch();
  const { setType, onClose } = props
  const [state, setState] = useState<{email: string, updated: Date}>();
  const { formState: { isSubmitSuccessful }, control, handleSubmit, reset, setError } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<IAuthState> = (data) => {
    dispatch(AuthActions.login(data)).then(
      async (response) => {
        if (response.confirmation) {
          setState({email: data.email, updated: new Date()})
        } else {
          await dispatch(AccountActions.account())
          onClose()
        }
      },
      error => {
        reset({
          email: data.email
        })
        setError('email', {
          type: 'manual',
          message: error
        })
      }
    )
  }

  return !state ? (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
        <Grid item>
          <Grid container direction="row" justifyContent="center" alignItems="stretch" spacing={2}>
            <Grid item>
              <Logo>GAMEPARADE</Logo>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Title>Вход</Title>
        </Grid>
        <Field>
          <Controller
            name="email"
            control={control}
            render={({
              field: { onChange, value }, fieldState
            }) => (
              <TextField
                required
                label="Электронная почта:"
                error={!!fieldState.error}
                onChange={onChange}
                value={value}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </Field>
        <Field>
          <Controller
            name="password"
            control={control}
            render={({
              field: { onChange, value }, fieldState
            }) => (
              <TextField
                required
                type="password"
                label="Пароль:"
                error={!!fieldState.error}
                onChange={onChange}
                value={value}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </Field>
        <Grid item>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Link onClick={() => setType('recovery')}>
                Забыли пароль?
              </Link>
            </Grid>
            <Grid item>
              <Button
                disabled={isSubmitSuccessful}
                size="large"
                type="submit"
              >
                Войти
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Grid item>
              <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2}>
                <Grid item>
                  <Description>У вас нет аккаунта?</Description>
                </Grid>
                <Grid item>
                  <Link onClick={() => setType('registration')}>
                    Зарегистрироваться
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  ) : <Confirmation state={state} onClose={onClose} />
}